
import { adminClient, emailOTPClient, organizationClient, twoFactorClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { cancelAllQueries, clearAuthData, saveAuthData } from './auth';
import { syncExtensionLogout } from './extension-auth-sync';
import { isDevelopment, SERVER_URL } from './env';
import { trpcClient } from './trpc';

const authClient = createAuthClient({
    baseURL: SERVER_URL,
    plugins: [
        organizationClient(),
        adminClient(),
        emailOTPClient(),  // Add email OTP plugin for client-side OTP methods
        twoFactorClient({
            // Redirect to 2FA verification page when required during login
            onTwoFactorRedirect: () => {
                window.location.href = '/verify-2fa';
            }
        }),
    ],
    debug: isDevelopment,
    fetchOptions: {
        credentials: 'include',
        auth: {
            type: "Bearer",
            // Only send Bearer token when one exists — sending an empty token
            // can cause the server to reject the request instead of falling
            // back to cookie-based authentication
            token: () => localStorage.getItem("auth:token") || undefined
        }
    }
});

// Original exports
const {
    signUp: originalSignUp,
    signIn: originalSignIn,
    signOut: originalSignOut,
    getSession: originalGetSession,
    useSession,
    verifyEmail,
    sendVerificationEmail,
} = authClient;


// Define types based on the original methods
type SignUpEmailParams = Parameters<typeof originalSignUp.email>[0];
type SignUpEmailOptions = Parameters<typeof originalSignUp.email>[1];
type SignOutOptions = Parameters<typeof originalSignOut>[0];

// Define a type for the session
interface AuthSession {
    session: {
        id: string;
        expiresAt: Date;
        createdAt: Date;
        updatedAt: Date;
        userId: string;
        token: string;
        ipAddress?: string | null;
        userAgent?: string | null;
        activeOrganizationId?: string | null;
    },
    user?: {
        id: string;
        name?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        jobRole?: string;
    };
    organization?: {
        id: string;
        name: string;
        slug: string;
        logo?: string | null;
        createdAt: string | Date;
        updatedAt: string | Date;
        metadata?: string | null;
    };
    activeWorkspace?: {
        id: string;
        title: string;
        description?: string | null;
        organizationId?: string | null;
        ownerId: string;
        createdAt: string | Date;
        updatedAt: string | Date;
    } | null;
    [key: string]: unknown;
}

// Helper to extract and store active organization and workspace from session
async function extractSessionData(authSession: AuthSession | null) {
    try {
        if (!authSession) return { organization: null, workspace: null };
        // console.log("extractSessionData", JSON.stringify(authSession, null, 2))
        const { session } = authSession
        if (session && session.token && session.expiresAt) {
            saveAuthData(session.token, session.expiresAt.getTime());
        }

        if (session.activeOrganizationId) {
            // Try to fetch workspaces for the organization
            try {
                const { organization: orgWithWorkspaces } = await trpcClient.organizations.getOrganizationWithWorkspaces.query({ id: session.activeOrganizationId });
                if (orgWithWorkspaces?.workspaces?.length > 0) {
                    if (!localStorage.getItem('auth:activeWorkspace')) {
                        // console.log('Using first workspace from organization:', workspace);
                        const workspace = orgWithWorkspaces.workspaces[0];
                        localStorage.setItem('auth:activeWorkspace', JSON.stringify(workspace));
                        localStorage.setItem('auth:activeWorkspaceId', workspace.id);
                    }
                }
                localStorage.setItem('auth:activeOrg', JSON.stringify(orgWithWorkspaces));
            } catch (error) {
                console.error('Error fetching workspaces:', error);
            }
        }
    } catch (error) {
        console.error('Error extracting session data:', error);
    }
}

// Helper to get stored active organization
export function getStoredActiveOrganization() {
    try {
        const storedOrg = localStorage.getItem('auth:activeOrg');
        return storedOrg ? JSON.parse(storedOrg) : null;
    } catch (error) {
        console.error('Error parsing stored organization:', error);
        return null;
    }
}

// Helper to get stored active workspace
export function getStoredActiveWorkspace() {
    try {
        const storedWorkspace = localStorage.getItem('auth:activeWorkspace');
        return storedWorkspace ? JSON.parse(storedWorkspace) : null;
    } catch (error) {
        console.error('Error parsing stored workspace:', error);
        return null;
    }
}

// };

const signUp = {
    ...originalSignUp,
    // Override methods to update localStorage
    async email(params: SignUpEmailParams, options?: SignUpEmailOptions) {
        console.log('Attempting email sign up');
        const result = await originalSignUp.email(params, options);
        return result;
    }
};

// Wrap signOut for non-component usage (preserving original behavior)
async function signOut(options?: SignOutOptions) {
  console.log('Signing out (basic)');

  // Cancel all pending queries BEFORE sign-out to prevent 401 errors
  cancelAllQueries();

  try {
    await syncExtensionLogout();
  } catch (error) {
    console.error('Failed to sync extension logout:', error);
  }

  // Tell server to invalidate session + clear HTTP-only cookie FIRST
  // while auth tokens are still available for the request
  let result;
  try {
    result = await originalSignOut(options);
  } catch (error) {
    console.error('Server sign-out request failed:', error);
  }

  // Clear client auth state AFTER server call so the request has valid tokens
  await clearAuthData()

  try {
    authClient.$store.notify('$sessionSignal')
  } catch (error) {
    console.error('Failed to notify session signal after sign out:', error)
  }

  console.log('Sign out complete, state cleared');
  return result;
}

// const signIn = {
//     ...originalSignIn,
//     // Override methods to update localStorage
//     async email(params: SignInEmailParams, options?: SignInEmailOptions) {
//         console.log('Attempting email sign in with params:', params);
//         try {
//             const result = await originalSignIn.email(params, options);
//             console.log('Sign in result:', JSON.stringify(result, null, 2));
//             // Check if login was successful by confirming the result exists
//             return result;
//         } catch (error) {
//             console.log("error login", error)
//         }
//     },
//     async social(params: SignInSocialParams, options?: SignInSocialOptions) {
//         console.log('Attempting social sign in with provider:', params.provider);
//         const result = await originalSignIn.social(params, options);
//         console.log("social login result", JSON.stringify(result, null, 2))
//         // if (result) {
//         //     setTimeout(async () => {
//         //         const session = await getSession();
//         //         if (session?.data) {
//         //             // Extract organization and workspace data directly from the session
//         //             await extractSessionData(session.data);
//         //         }
//         //     }, 300); // Give some time for the session to be established
//         // }

//         return result;
//     }
// }

// Remove the custom getSession wrapper and use vanilla better-auth
const getSession = async () => {
    const { data: session, error } = await originalGetSession();
    if (error || !session) {
        return null;
    }
    
    // Only handle basic auth data storage
    localStorage.setItem("auth:session", JSON.stringify(session));
    localStorage.setItem("auth:token", session.session.token);
    localStorage.setItem("auth:expiry", session.session.expiresAt.getTime().toString());
    return session;
}

// Remove extractSessionData function entirely
// Remove getStoredActiveOrganization and getStoredActiveWorkspace helpers

const signIn = originalSignIn;

// Export twoFactor methods for MFA setup and verification
const { twoFactor } = authClient;

export {
    getSession, signIn, signOut,
    signUp,
    useSession,
    verifyEmail,
    sendVerificationEmail,
    authClient,
    twoFactor,
};
